/**
 * JWT Authentication Utilities
 */

import { DecodedToken } from '../types/auth';

/**
 * Get the JWT token from localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

/**
 * Set the JWT token in localStorage
 */
export const setToken = (token: string, expiresIn = 86400): void => {
  localStorage.setItem('auth_token', token);
  const expirationTime = Date.now() + expiresIn * 1000;
  localStorage.setItem('token_expiration', expirationTime.toString());
};

/**
 * Remove the JWT token from localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('token_expiration');
};

/**
 * Check if the token exists and is not expired
 */
export const isTokenValid = (): boolean => {
  const token = getToken();
  if (!token) {
    return false;
  }
  
  const expiration = localStorage.getItem('token_expiration');
  if (!expiration) {
    return false;
  }
  
  return parseInt(expiration) > Date.now();
};

/**
 * Decode the JWT token payload (without verification)
 * @param customToken Optional token to decode (if not provided, uses token from storage)
 */
export const decodeToken = (customToken?: string): DecodedToken | null => {
  const token = customToken || getToken();
  if (!token) {
    return null;
  }
  
  try {
    // Split the token and get the payload part
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    
    return {
      userId: payload.userId,
      email: payload.email,
      role: payload.role,
      exp: payload.exp,
      // Include the new custom properties for role-based access control
      actingAsAdmin: payload.actingAsAdmin || false,
      selectedTenantId: payload.selectedTenantId || null
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get the number of seconds until the token expires
 */
export const getTokenTimeRemaining = (): number => {
  const expiration = localStorage.getItem('token_expiration');
  if (!expiration) {
    return 0;
  }
  
  const expirationTime = parseInt(expiration);
  const timeRemaining = expirationTime - Date.now();
  
  return Math.max(0, Math.floor(timeRemaining / 1000));
};